import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

/**
 * Error boundary component that catches JavaScript errors in child components.
 * Displays a fallback UI instead of crashing the entire application.
 */
export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
        };
    }

    static getDerivedStateFromError(error: Error): Partial<State> {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        this.setState({ errorInfo });
        console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    handleReset = (): void => {
        this.setState({ hasError: false, error: null, errorInfo: null });
    };

    handleReload = (): void => {
        window.location.reload();
    };

    render(): ReactNode {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div style={styles.container}>
                    <div style={styles.content}>
                        <h1 style={styles.title}>Something went wrong</h1>
                        <p style={styles.message}>
                            An unexpected error occurred. You can try to recover or reload the page.
                        </p>
                        {this.state.error && (
                            <details style={styles.details}>
                                <summary style={styles.summary}>Error details</summary>
                                <pre style={styles.errorText}>
                                    {this.state.error.toString()}
                                    {this.state.errorInfo?.componentStack}
                                </pre>
                            </details>
                        )}
                        <div style={styles.buttons}>
                            <button style={styles.button} onClick={this.handleReset}>
                                Try Again
                            </button>
                            <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={this.handleReload}>
                                Reload Page
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

const styles: Record<string, React.CSSProperties> = {
    container: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#1a1a2e',
        color: '#e0e0e0',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        padding: '20px',
    },
    content: {
        maxWidth: '600px',
        textAlign: 'center',
    },
    title: {
        fontSize: '24px',
        fontWeight: 600,
        marginBottom: '16px',
        color: '#ff6b6b',
    },
    message: {
        fontSize: '16px',
        marginBottom: '24px',
        color: '#b0b0b0',
    },
    details: {
        marginBottom: '24px',
        textAlign: 'left',
    },
    summary: {
        cursor: 'pointer',
        color: '#888',
        marginBottom: '8px',
    },
    errorText: {
        backgroundColor: '#0d0d1a',
        padding: '16px',
        borderRadius: '8px',
        fontSize: '12px',
        overflow: 'auto',
        maxHeight: '200px',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
    },
    buttons: {
        display: 'flex',
        gap: '12px',
        justifyContent: 'center',
    },
    button: {
        padding: '12px 24px',
        fontSize: '14px',
        fontWeight: 500,
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        backgroundColor: '#4a9eff',
        color: '#fff',
        transition: 'background-color 0.2s',
    },
    secondaryButton: {
        backgroundColor: '#333',
        color: '#e0e0e0',
    },
};

export default ErrorBoundary;
